import { HTMLAttributes } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import Link from 'next/link'

import Container from '@/components/base/container'
import CtaButton from '@/components/base/cta-button'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import { relatedStuffRevealAnimation } from '@/components/sections/animations/related-stuff'
import Spacer from '@/components/sections/spacer'

import Grid from '../base/grid'
import { cn } from '@/lib/utils'
import { WPImage, WPLink } from '@/types'

type RelatedStuffProject = {
  title?: string
  text?: string
  country?: string
  link?: string
  image?: WPImage
}

type RelatedStuffProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    title?: string
    pretitle?: string
    link?: WPLink
    text?: string
    collection?: RelatedStuffProject[]
  }>

const RelatedStuff = ({
  title,
  pretitle,
  link,
  collection = [],
  className,
  ...props
}: RelatedStuffProps) => {
  const [emblaRef] = useEmblaCarousel({
    align: 'start',
    dragFree: true,
    containScroll: 'trimSnaps'
  })

  return (
    <Motion animation={relatedStuffRevealAnimation}>
      {(motionRef) => (
        <Section
          ref={motionRef}
          className={cn('max-w-full overflow-hidden', className)}
          {...props}
        >
          <Spacer variant="xl" />
          <Container>
            <div className="mb-spacing-sm flex items-end justify-between gap-spacing-sm border-b border-border-secondary-light pb-spacing-xs dark:border-border-secondary-dark">
              <div className="flex flex-col gap-spacing-xs">
                {pretitle ? (
                  <Typography
                    variant="h6"
                    component="p"
                    className="text-s-h6 text-text-primary-light md:text-d-h6 dark:text-text-primary-dark"
                    data-motion-pretitle
                  >
                    {pretitle}
                  </Typography>
                ) : null}
                {title ? (
                  <Typography
                    variant="h3"
                    component="h2"
                    className="text-h3 md:text-d-h2"
                    data-motion-title
                  >
                    {title}
                  </Typography>
                ) : null}
              </div>

              {link?.url ? (
                <CtaButton
                  href={link?.url}
                  variant="secondary"
                  typography="regular"
                  className="hidden min-w-[20rem] md:flex"
                >
                  {link?.title}
                </CtaButton>
              ) : null}
            </div>
          </Container>
          <Container>
            <div
              ref={emblaRef}
              className={cn('overflow-hidden', {
                'pl-margin': collection?.length <= 1
              })}
            >
              <Grid className={cn('')}>
                {collection.map((project, index) => (
                  <article
                    key={`related-project-${project.title}-${index}`}
                    className="col-span-2 flex-shrink-0 md:col-span-6 lg:col-span-6"
                    data-motion-item
                  >
                    <div className="relative mb-spacing-sm aspect-[4/3] overflow-hidden">
                      <Link
                        className="absolute inset-0 z-10 flex h-full w-full"
                        href={project.link || '#'}
                        scroll={false}
                        aria-hidden="true"
                        tabIndex={-1}
                      >
                        <img
                          src={project.image?.url}
                          alt=""
                          className="absolute inset-0 h-full w-full object-cover"
                        />
                      </Link>
                    </div>

                    <div className="place-self-auto md:place-self-end">
                      <CtaButton
                        href={project.link || '#'}
                        variant="link"
                        typography="h6"
                        className="w-full"
                      >
                        {project.title}
                      </CtaButton>

                      <Typography
                        variant="regular"
                        className="mt-[14px] pb-spacing-xs text-text-secondary-light dark:text-text-secondary-dark"
                      >
                        {project.text}
                      </Typography>
                    </div>
                  </article>
                ))}
              </Grid>
            </div>
          </Container>
          <Container className="mt-spacing-sm md:hidden">
            {link?.url ? (
              <CtaButton
                href={link?.url}
                variant="secondary"
                typography="regular"
                className="w-full"
              >
                {link?.title}
              </CtaButton>
            ) : null}
          </Container>
          <Spacer variant="xl" />
        </Section>
      )}
    </Motion>
  )
}

export default RelatedStuff
